import { useState, useCallback } from 'react';
import { Check, Copy as CopyIcon } from 'lucide-react';
import { useClipboard } from '@/hooks/useClipboard';

type CopyButtonProps = {
  getText: () => string;
  /** Visible button label. Pass an empty string to render an icon-only button. Defaults to "Copy". */
  label?: string;
  /**
   * Accessible name for icon-only buttons (label === "").
   * Required when no visible label is shown so screen readers can announce
   * the action (WCAG 2.1 SC 4.1.2). Ignored when a non-empty label is present.
   */
  accessibleName?: string;
};

/** Copy-to-clipboard button with visual feedback (F-034). */
export function CopyButton({ getText, label = 'Copy', accessibleName }: CopyButtonProps) {
  const { copy } = useClipboard();
  const [copied, setCopied] = useState(false);

  if (import.meta.env.DEV && !label && !accessibleName) {
    console.warn(
      'CopyButton: an icon-only button (label="") requires an accessibleName prop so ' +
        'screen readers can announce it. Pass accessibleName (e.g. "Copy Cmax ratio") or ' +
        'set a visible label.',
    );
  }

  const onClick = useCallback(async () => {
    await copy(getText());
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }, [getText, copy]);

  const iconOnly = !label;
  const accessibleLabel = label || accessibleName || '';

  return (
    <button
      type="button"
      className="btn btn-sm btn-ghost"
      onClick={onClick}
      aria-label={iconOnly ? accessibleLabel : undefined}
      title={iconOnly ? accessibleLabel : undefined}
    >
      {copied
        ? iconOnly
          ? <Check size={14} aria-hidden="true" />
          : (
            <>
              <Check size={14} aria-hidden="true" /> Copied
            </>
          )
        : iconOnly
          ? <CopyIcon size={14} aria-hidden="true" />
          : (
            <>
              <CopyIcon size={14} aria-hidden="true" /> {label}
            </>
          )}
    </button>
  );
}
